// ./src/config/efficiencyThresholds.ts
// Efficiency thresholds configuration for fuel economy ratings
// Used by carHelpers for efficiency status calculations

import { VehicleType } from "./bodyTypes";

// ============================================
// Types
// ============================================

export type EfficiencyStatus = "excellent" | "good" | "average" | "poor";

export interface EfficiencyThresholds {
  /** Below this value is "excellent" */
  excellent: number;
  /** Below this value is "good" */
  good: number;
  /** Below this value is "average", at or above is "poor" */
  average: number;
}

// ============================================
// Fuel Efficiency Thresholds (L/100km)
// Lower is better
// ============================================

export const FUEL_EFFICIENCY_THRESHOLDS: Record<VehicleType, EfficiencyThresholds> = {
  car: {
    excellent: 6,  // < 6 L/100km
    good: 8,       // < 8 L/100km
    average: 10,   // < 10 L/100km, >= 10 is poor
  },
  motorcycle: {
    excellent: 3,
    good: 4,
    average: 5,
  },
  truck: {
    excellent: 10,
    good: 13,
    average: 16,
  },
  other: {
    excellent: 7,
    good: 9,
    average: 12,
  },
};

// ============================================
// Electric Efficiency Thresholds (kWh/100km)
// Lower is better
// ============================================

export const ELECTRIC_EFFICIENCY_THRESHOLDS: Record<VehicleType, EfficiencyThresholds> = {
  car: {
    excellent: 14, // < 14 kWh/100km (very efficient EV)
    good: 18,      // < 18 kWh/100km
    average: 22,   // < 22 kWh/100km, >= 22 is poor
  },
  motorcycle: {
    excellent: 4,
    good: 6,
    average: 8,
  },
  truck: {
    excellent: 25,
    good: 35,
    average: 45,
  },
  other: {
    excellent: 16,
    good: 20,
    average: 25,
  },
};

// ============================================
// Efficiency Status Colors (for UI)
// ============================================

export const EFFICIENCY_STATUS_COLORS: Record<EfficiencyStatus, string> = {
  excellent: "green",
  good: "teal",
  average: "yellow",
  poor: "red",
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get efficiency status based on consumption value
 * @param efficiency - Efficiency value (L/100km or kWh/100km)
 * @param vehicleType - Type of vehicle
 * @param isElectric - Whether vehicle is electric (uses kWh/100km thresholds)
 * @returns Efficiency status or null if value is invalid
 */
export const getEfficiencyStatus = (
  efficiency: number | null | undefined,
  vehicleType: VehicleType = "car",
  isElectric: boolean = false
): EfficiencyStatus | null => {
  if (efficiency === null || efficiency === undefined || efficiency <= 0) {
    return null;
  }

  const thresholds = isElectric
    ? ELECTRIC_EFFICIENCY_THRESHOLDS[vehicleType]
    : FUEL_EFFICIENCY_THRESHOLDS[vehicleType];

  if (efficiency < thresholds.excellent) {
    return "excellent";
  }
  if (efficiency < thresholds.good) {
    return "good";
  }
  if (efficiency < thresholds.average) {
    return "average";
  }
  return "poor";
};

/**
 * Get color for efficiency status
 * @param status - Efficiency status
 * @returns Color key for theme
 */
export const getEfficiencyStatusColor = (
  status: EfficiencyStatus | null | undefined
): string => {
  if (!status) return "gray";
  return EFFICIENCY_STATUS_COLORS[status] ?? "gray";
};

/**
 * Get thresholds for a vehicle type
 * @param vehicleType - Type of vehicle
 * @param isElectric - Whether vehicle is electric
 * @returns Efficiency thresholds
 */
export const getEfficiencyThresholds = (
  vehicleType: VehicleType = "car",
  isElectric: boolean = false
): EfficiencyThresholds => {
  return isElectric
    ? ELECTRIC_EFFICIENCY_THRESHOLDS[vehicleType]
    : FUEL_EFFICIENCY_THRESHOLDS[vehicleType];
};